import React, { useState, useEffect } from 'react';
import { Tab } from '@headlessui/react';
import {
  InformationCircleIcon,
  IdentificationIcon,
  HomeIcon,
  BeakerIcon,
  HeartIcon,
  BanknotesIcon,
  ExclamationTriangleIcon,
  PhotoIcon,
  HeartIcon as HealthIcon,
} from '@heroicons/react/24/outline';
import type { Bird, CreateBirdDto } from '../api/birds';
import { updateBirdAvatar } from '../api/birds';
import { Gender, BirdStatus, GenderDeterminationMethod, HealthStatus, VaccinationStatus } from '../utils/enums';
import { getSpecies, Species } from '../api/species';
import toast from 'react-hot-toast';
import { formatDateForInput } from '../utils/formatters';
import BirdGenotypeEditor from './genetics/BirdGenotypeEditor';
import CageSelect from './CageSelect';
import BirdPhotos from './PhotoGallery/BirdPhotos';
import SpeciesCombobox from './SpeciesCombobox';
import TabbedFormActions, { isNonEmptyString } from './ui/TabbedFormActions';
import {
  BrandModal,
  BrandModalBody,
  BrandModalHeader,
} from './ui/BrandModal';
import AmountInput from './ui/AmountInput';

interface EditBirdModalProps {
  isOpen: boolean;
  onClose: () => void;
  onEdit: (id: string, birdData: Partial<CreateBirdDto>) => void;
  onBirdUpdate?: (bird: Bird) => void;
  bird: Bird;
}

type FormData = {
  bandId: string;
  speciesId: string;
  mutationName: string;
  gender: Gender;
  genderDeterminationMethod: GenderDeterminationMethod;
  status: BirdStatus;
  sellPrice: string;
  hatchingDate: string;
  bandingDate: string;
  cageNumber: string;
  // Health-related fields
  healthStatus: string;
  vaccinationStatus: string;
  weight: string;
  lastHealthCheck: string;
  nextHealthCheck: string;
  healthNotes: string;
  currentMedications: string;
  provenance: {
    type: 'BRED' | 'BOUGHT' | 'OTHER';
    fatherId: string;
    motherId: string;
    sellerName: string;
    sellerContact: string;
    dateAcquired: string;
    price: string;
    notes: string;
  };
  phenotype: string;
  genotype: string;
};

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(' ');
}

export default function EditBirdModal({ isOpen, onClose, onEdit, onBirdUpdate, bird }: EditBirdModalProps) {
  // Early return if bird data is incomplete
  if (!bird || !bird.id || !bird.bandId) {
    return null;
  }

  const [selectedTab, setSelectedTab] = useState(0);
  const [species, setSpecies] = useState<Species[]>([]);
  const [formData, setFormData] = useState<FormData>({
    bandId: '',
    speciesId: '',
    mutationName: '',
    gender: Gender.UNKNOWN,
    genderDeterminationMethod: GenderDeterminationMethod.NOT_SEXED,
    status: BirdStatus.AVAILABLE,
    sellPrice: '',
    hatchingDate: '',
    bandingDate: '',
    cageNumber: '',
    healthStatus: '',
    vaccinationStatus: '',
    weight: '',
    lastHealthCheck: '',
    nextHealthCheck: '',
    healthNotes: '',
    currentMedications: '',
    provenance: {
      type: 'BRED',
      fatherId: '',
      motherId: '',
      sellerName: '',
      sellerContact: '',
      dateAcquired: '',
      price: '',
      notes: ''
    },
    phenotype: '',
    genotype: ''
  });

  useEffect(() => {
    if (isOpen) {
      loadSpecies();
    }
  }, [isOpen]);

  const loadSpecies = async () => {
    try {
      const speciesData = await getSpecies();
      setSpecies(
        [...speciesData].sort((a, b) =>
          a.commonName.localeCompare(b.commonName, undefined, { sensitivity: 'base' }),
        ),
      );
    } catch (err) {
      console.error('Error loading species:', err);
      toast.error('Failed to load species data');
    }
  };

  useEffect(() => {
    if (bird && bird.id && bird.bandId) {
      setFormData({
        bandId: bird.bandId || '',
        speciesId: bird.species?.id || bird.speciesId || '',
        mutationName: bird.mutationName || '',
        gender: bird.gender || Gender.UNKNOWN,
        genderDeterminationMethod: bird.genderDeterminationMethod || GenderDeterminationMethod.NOT_SEXED,
        status: bird.status || BirdStatus.AVAILABLE,
        sellPrice: bird.sellPrice?.toString() || '',
        hatchingDate: formatDateForInput(bird.hatchingDate),
        bandingDate: formatDateForInput(bird.bandingDate),
        cageNumber: bird.cageNumber || '',
        healthStatus: bird.healthStatus || '',
        vaccinationStatus: bird.vaccinationStatus || '',
        weight: bird.weight?.toString() || '',
        lastHealthCheck: formatDateForInput(bird.lastHealthCheck),
        nextHealthCheck: formatDateForInput(bird.nextHealthCheck),
        healthNotes: bird.healthNotes || '',
        currentMedications: Array.isArray(bird.currentMedications) ? bird.currentMedications.join(', ') : (bird.currentMedications || ''),
        provenance: {
          type: bird.provenance?.type || 'OTHER',
          fatherId: bird.provenance?.fatherId || '',
          motherId: bird.provenance?.motherId || '',
          sellerName: bird.provenance?.sellerName || '',
          sellerContact: bird.provenance?.sellerContact || '',
          dateAcquired: formatDateForInput(bird.provenance?.dateAcquired),
          price: bird.provenance?.price?.toString() || '',
          notes: bird.provenance?.notes || ''
        },
        phenotype: bird.phenotype || '',
        genotype: bird.genotype || ''
      });
    }
  }, [bird]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    if (name.startsWith('provenance.')) {
      const provenanceField = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        provenance: {
          ...prev.provenance,
          [provenanceField]: value
        }
      }));
    } else if (name === 'gender') {
      setFormData(prev => ({ ...prev, [name]: value as Gender }));
    } else if (name === 'genderDeterminationMethod') {
      setFormData(prev => ({ ...prev, [name]: value as GenderDeterminationMethod }));
    } else if (name === 'status') {
      setFormData(prev => ({ ...prev, [name]: value as BirdStatus }));
    } else if (name === 'provenance.type') {
      setFormData(prev => ({
        ...prev,
        provenance: {
          ...prev.provenance,
          type: value as 'BRED' | 'BOUGHT' | 'OTHER'
        }
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      // Validate required fields
      if (!formData.bandId.trim()) {
        toast.error('Band ID is required');
        return;
      }
      
      if (!formData.speciesId) {
        toast.error('Species is required');
        return;
      }
      
      if (!formData.hatchingDate) {
        toast.error('Hatching date is required');
        return;
      }
      
      if (!formData.bandingDate) {
        toast.error('Banding date is required');
        return;
      }
      
      // Convert string values to appropriate types
      const formattedData: Partial<CreateBirdDto> = {
        ...formData,
        sellPrice: formData.sellPrice ? parseFloat(formData.sellPrice) : undefined,
        provenance: {
          ...formData.provenance,
          price: formData.provenance.price ? parseFloat(formData.provenance.price) : undefined,
          dateAcquired: formData.provenance.dateAcquired || undefined
        },
        // Send dates as YYYY-MM-DD format, let backend handle conversion
        hatchingDate: formData.hatchingDate,
        bandingDate: formData.bandingDate
      };

      // Remove empty string values to avoid validation issues
      if (!formattedData.sellPrice) delete formattedData.sellPrice;
      if (!formattedData.provenance?.price) delete formattedData.provenance.price;
      if (!formattedData.provenance?.dateAcquired) delete formattedData.provenance.dateAcquired;
      if (!formattedData.mutationName?.trim()) delete formattedData.mutationName;
      if (!formattedData.phenotype?.trim()) delete formattedData.phenotype;
      if (!formattedData.genotype?.trim()) delete formattedData.genotype;

      // Clean up provenance nested fields
      Object.keys(formattedData.provenance).forEach(key => {
        const value = formattedData.provenance[key];
        if (typeof value === 'string' && !value.trim()) {
          delete formattedData.provenance[key];
        }
      });

      onEdit(bird.id, formattedData);
    } catch (error) {
      console.error('Error preparing bird data for update:', error);
      toast.error('Error preparing bird data. Please check your inputs.');
    }
  };

  const categories = [
    {
      name: 'Basic Info',
      icon: InformationCircleIcon,
      content: <BasicInfoTab formData={formData} handleChange={handleChange} species={species} />
    },
    {
      name: 'Identity',
      icon: IdentificationIcon,
      content: <IdentityTab formData={formData} handleChange={handleChange} />
    },
    {
      name: 'Housing',
      icon: HomeIcon,
      content: <HousingTab formData={formData} handleChange={handleChange} />
    },
    {
      name: 'Photos',
      icon: PhotoIcon,
      content: (
        <div className="space-y-4">
          <div>
            <h3 className="text-lg font-medium text-slate-900">Photos</h3>
            <p className="text-sm text-slate-500 mt-1">
              Upload photos and choose an avatar for this bird.
            </p>
          </div>
          <BirdPhotos
            birdId={bird.id}
            initialPhotos={bird.photos || []}
            selectedAvatar={bird.avatarPhotoId}
            onAvatarChange={async (photoId) => {
              const updated = await updateBirdAvatar(bird.id, photoId);
              onBirdUpdate?.(updated);
            }}
            onPhotosChange={(photos) => {
              onBirdUpdate?.({ ...bird, photos });
            }}
          />
        </div>
      ),
    },
    {
      name: 'Health',
      icon: HeartIcon,
      content: <HealthTab formData={formData} handleChange={handleChange} />
    },
    {
      name: 'Provenance',
      icon: ExclamationTriangleIcon,
      content: <ProvenanceTab formData={formData} handleChange={handleChange} />
    },
    {
      name: 'Sale Info',
      icon: BanknotesIcon,
      content: <SaleInfoTab bird={bird} />
    },
    {
      name: 'Genetics',
      icon: BeakerIcon,
      content: (
        <BirdGenotypeEditor
          speciesId={formData.speciesId}
          birdId={bird.id}
          gender={formData.gender}
          autoSave
        />
      ),
    }
  ];

  const canSubmit =
    isNonEmptyString(formData.bandId) &&
    isNonEmptyString(formData.speciesId) &&
    isNonEmptyString(formData.hatchingDate) &&
    isNonEmptyString(formData.bandingDate);

  return (
    <BrandModal open={isOpen} onClose={onClose} maxWidthClass="max-w-4xl">
      <BrandModalHeader title={`Edit Bird: ${bird.bandId}`} onClose={onClose} />
      <BrandModalBody>
        <form onSubmit={handleSubmit} className="w-full">
          <div className="w-full">
            <Tab.Group selectedIndex={selectedTab} onChange={setSelectedTab}>
              <Tab.List className="flex space-x-1 rounded-xl bg-gray-100 p-1 mb-6 overflow-x-auto">
                {categories.map((category) => (
                  <Tab
                    key={category.name}
                    className={({ selected }) =>
                      classNames(
                        'flex-shrink-0 rounded-lg py-2.5 px-3 text-sm font-medium leading-5 flex items-center justify-center space-x-2',
                        'ring-white ring-opacity-60 ring-offset-2 ring-offset-indigo-400 focus:outline-none focus:ring-2',
                        selected
                          ? 'bg-white shadow text-cyan-700 border border-indigo-200'
                          : 'text-slate-600 hover:bg-white/[0.8] hover:text-gray-800'
                      )
                    }
                  >
                    <category.icon className="h-4 w-4" />
                    <span className="hidden sm:block whitespace-nowrap">{category.name}</span>
                  </Tab>
                ))}
              </Tab.List>
              <Tab.Panels className="mt-2">
                {categories.map((category, idx) => (
                  <Tab.Panel
                    key={idx}
                    className={classNames(
                      'rounded-xl bg-white p-6 shadow-sm border min-h-[400px]',
                      'ring-white ring-opacity-60 ring-offset-2 ring-offset-indigo-400 focus:outline-none focus:ring-2'
                    )}
                  >
                    {category.content}
                  </Tab.Panel>
                ))}
              </Tab.Panels>
            </Tab.Group>
          </div>

          <TabbedFormActions
            onCancel={onClose}
            tabCount={categories.length}
            selectedTab={selectedTab}
            onTabChange={setSelectedTab}
            canSubmit={canSubmit}
            submitLabel="Save Changes"
          />
        </form>
      </BrandModalBody>
    </BrandModal>
  );
}


// Tab Components
interface TabProps {
  formData: FormData;
  handleChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => void;
  species?: Species[];
}

function BasicInfoTab({ formData, handleChange, species }: TabProps) {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-slate-900 mb-4">Basic Information</h3>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <label htmlFor="bandId" className="block text-sm font-medium text-slate-700">
              Band ID <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="bandId"
              id="bandId"
              value={formData.bandId}
              onChange={handleChange}
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
              required
            />
          </div>

          <div>
            <label htmlFor="speciesId" className="block text-sm font-medium text-slate-700">
              Species <span className="text-red-500">*</span>
            </label>
            <SpeciesCombobox
              id="speciesId"
              species={species || []}
              value={formData.speciesId}
              onChange={(speciesId) =>
                handleChange({
                  target: { name: 'speciesId', value: speciesId },
                } as React.ChangeEvent<HTMLSelectElement>)
              }
              required
              placeholder="Type to search species…"
            />
          </div>

          <div>
            <label htmlFor="mutationName" className="block text-sm font-medium text-slate-700">
              Mutation Name
            </label>
            <input
              type="text"
              name="mutationName"
              id="mutationName"
              value={formData.mutationName}
              onChange={handleChange}
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
              placeholder="e.g., Lutino, Pied, Normal"
            />
          </div>

          <div>
            <label htmlFor="status" className="block text-sm font-medium text-slate-700">
              Status <span className="text-red-500">*</span>
            </label>
            <select
              name="status"
              id="status"
              value={formData.status}
              onChange={handleChange}
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
              required
            >
              <option value={BirdStatus.AVAILABLE}>Available</option>
              <option value={BirdStatus.PAIRED}>Paired</option>
              <option value={BirdStatus.FOR_SALE}>For Sale</option>
              <option value={BirdStatus.DECEASED}>Deceased</option>
              <option value={BirdStatus.LOST}>Lost</option>
              <option value={BirdStatus.DONATED}>Donated</option>
            </select>
          </div>

          {formData.status === BirdStatus.FOR_SALE && (
            <div>
              <label htmlFor="sellPrice" className="block text-sm font-medium text-slate-700">
                Sale Price (ZAR) <span className="text-red-500">*</span>
              </label>
              <AmountInput
                id="sellPrice"
                name="sellPrice"
                value={formData.sellPrice}
                onChange={(v) =>
                  handleChange({
                    target: { name: 'sellPrice', value: v != null ? String(v) : '' },
                  } as React.ChangeEvent<HTMLInputElement>)
                }
                required
                min={0}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function IdentityTab({ formData, handleChange }: TabProps) {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-slate-900 mb-4">Identity & Gender</h3>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <label htmlFor="gender" className="block text-sm font-medium text-slate-700">
              Gender <span className="text-red-500">*</span>
            </label>
            <select
              name="gender"
              id="gender"
              value={formData.gender}
              onChange={handleChange}
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
              required
            >
              <option value={Gender.UNKNOWN}>Unknown</option>
              <option value={Gender.MALE}>Male</option>
              <option value={Gender.FEMALE}>Female</option>
            </select>
          </div>

          <div>
            <label htmlFor="genderDeterminationMethod" className="block text-sm font-medium text-slate-700">
              Gender Determination Method <span className="text-red-500">*</span>
            </label>
            <select
              name="genderDeterminationMethod"
              id="genderDeterminationMethod"
              value={formData.genderDeterminationMethod}
              onChange={handleChange}
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
              required
            >
              <option value={GenderDeterminationMethod.NOT_SEXED}>Not Sexed</option>
              <option value={GenderDeterminationMethod.VISUALLY_SEXED}>Visually Sexed</option>
              <option value={GenderDeterminationMethod.DNA_SEXING}>DNA Sexing</option>
              <option value={GenderDeterminationMethod.SURGICAL_SEXING}>Surgical Sexing</option>
            </select>
          </div>

          <div>
            <label htmlFor="hatchingDate" className="block text-sm font-medium text-slate-700">
              Hatching Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              name="hatchingDate"
              id="hatchingDate"
              value={formData.hatchingDate}
              onChange={handleChange}
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
              required
            />
          </div>

          <div>
            <label htmlFor="bandingDate" className="block text-sm font-medium text-slate-700">
              Banding Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              name="bandingDate"
              id="bandingDate"
              value={formData.bandingDate}
              onChange={handleChange}
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
              required
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function HousingTab({ formData, handleChange }: TabProps) {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-slate-900 mb-4">Housing Information</h3>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <label htmlFor="cageNumber" className="block text-sm font-medium text-slate-700">
              Cage Number <span className="text-red-500">*</span>
            </label>
            <CageSelect
              id="cageNumber"
              name="cageNumber"
              value={formData.cageNumber}
              onChange={(cageNumber) =>
                handleChange({ target: { name: 'cageNumber', value: cageNumber } } as React.ChangeEvent<HTMLInputElement>)
              }
              required
              placeholder="Search or add a cage number…"
            />
            <p className="mt-2 text-sm text-slate-500">
              Select the cage or housing location where this bird is currently kept.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function HealthTab({ formData, handleChange }: TabProps) {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-slate-900 mb-4">Health Information</h3>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <label htmlFor="healthStatus" className="block text-sm font-medium text-slate-700">
              Health Status
            </label>
            <select
              name="healthStatus"
              id="healthStatus"
              value={formData.healthStatus}
              onChange={handleChange}
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
            >
              <option key="empty-option-1753461297304" value="">Select a health status</option>
              <option value={HealthStatus.EXCELLENT}>Excellent</option>
              <option value={HealthStatus.GOOD}>Good</option>
              <option value={HealthStatus.FAIR}>Fair</option>
              <option value={HealthStatus.POOR}>Poor</option>
              <option value={HealthStatus.CRITICAL}>Critical</option>
              <option value={HealthStatus.SICK}>Sick</option>
              <option value={HealthStatus.UNDER_OBSERVATION}>Under Observation</option>
              <option value={HealthStatus.RECOVERING}>Recovering</option>
              <option value={HealthStatus.QUARANTINE}>Quarantine</option>
              <option value={HealthStatus.UNKNOWN}>Unknown</option>
            </select>
          </div>

          <div>
            <label htmlFor="vaccinationStatus" className="block text-sm font-medium text-slate-700">
              Vaccination Status
            </label>
            <select
              name="vaccinationStatus"
              id="vaccinationStatus"
              value={formData.vaccinationStatus}
              onChange={handleChange}
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
            >
              <option key="empty-option-1753461297304" value="">Select a vaccination status</option>
              <option value={VaccinationStatus.UP_TO_DATE}>Up to Date</option>
              <option value={VaccinationStatus.OVERDUE}>Overdue</option>
              <option value={VaccinationStatus.PARTIAL}>Partial</option>
              <option value={VaccinationStatus.NOT_STARTED}>Not Started</option>
              <option value={VaccinationStatus.UNKNOWN}>Unknown</option>
            </select>
          </div>

          <div>
            <label htmlFor="weight" className="block text-sm font-medium text-slate-700">
              Weight (grams)
            </label>
            <input
              type="number"
              name="weight"
              id="weight"
              value={formData.weight}
              onChange={handleChange}
              step="0.1"
              min="0"
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
              placeholder="e.g., 150.5"
            />
          </div>

          <div>
            <label htmlFor="lastHealthCheck" className="block text-sm font-medium text-slate-700">
              Last Health Check
            </label>
            <input
              type="date"
              name="lastHealthCheck"
              id="lastHealthCheck"
              value={formData.lastHealthCheck}
              onChange={handleChange}
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
            />
          </div>

          <div>
            <label htmlFor="nextHealthCheck" className="block text-sm font-medium text-slate-700">
              Next Health Check
            </label>
            <input
              type="date"
              name="nextHealthCheck"
              id="nextHealthCheck"
              value={formData.nextHealthCheck}
              onChange={handleChange}
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
            />
          </div>
        </div>

        <div className="mt-6 space-y-4">
          <div>
            <label htmlFor="healthNotes" className="block text-sm font-medium text-slate-700">
              Health Notes
            </label>
            <textarea
              name="healthNotes"
              id="healthNotes"
              value={formData.healthNotes}
              onChange={handleChange}
              rows={3}
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
              placeholder="Additional notes about the bird's health..."
            />
          </div>

          <div>
            <label htmlFor="currentMedications" className="block text-sm font-medium text-slate-700">
              Current Medications
            </label>
            <textarea
              name="currentMedications"
              id="currentMedications"
              value={formData.currentMedications}
              onChange={handleChange}
              rows={3}
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
              placeholder="List any current medications (separate with commas)..."
            />
            <p className="mt-1 text-sm text-slate-500">
              Separate multiple medications with commas
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function ProvenanceTab({ formData, handleChange }: TabProps) {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-slate-900 mb-4">Provenance & Origin</h3>
        
        <div className="space-y-6">
          <div>
            <label htmlFor="provenance.type" className="block text-sm font-medium text-slate-700">
              Provenance Type <span className="text-red-500">*</span>
            </label>
            <select
              name="provenance.type"
              id="provenance.type"
              value={formData.provenance.type}
              onChange={handleChange}
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
              required
            >
              <option key="BRED-1753461297304" value="BRED">Bred</option>
              <option key="BOUGHT-1753461297304" value="BOUGHT">Bought</option>
              <option key="OTHER-1753461297304" value="OTHER">Other</option>
            </select>
          </div>

          {formData.provenance.type === 'BRED' && (
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <h4 className="text-md font-medium text-green-900 mb-3">Breeding Information</h4>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="provenance.fatherId" className="block text-sm font-medium text-slate-700">
                    Father Band ID
                  </label>
                  <input
                    type="text"
                    name="provenance.fatherId"
                    id="provenance.fatherId"
                    value={formData.provenance.fatherId}
                    onChange={handleChange}
                    className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
                    placeholder="e.g., M123"
                  />
                </div>

                <div>
                  <label htmlFor="provenance.motherId" className="block text-sm font-medium text-slate-700">
                    Mother Band ID
                  </label>
                  <input
                    type="text"
                    name="provenance.motherId"
                    id="provenance.motherId"
                    value={formData.provenance.motherId}
                    onChange={handleChange}
                    className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
                    placeholder="e.g., F456"
                  />
                </div>
              </div>
            </div>
          )}

          {formData.provenance.type === 'BOUGHT' && (
            <div className="bg-cyan-50 p-4 rounded-lg border border-blue-200">
              <h4 className="text-md font-medium text-blue-900 mb-3">Purchase Information</h4>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="provenance.sellerName" className="block text-sm font-medium text-slate-700">
                    Seller Name
                  </label>
                  <input
                    type="text"
                    name="provenance.sellerName"
                    id="provenance.sellerName"
                    value={formData.provenance.sellerName}
                    onChange={handleChange}
                    className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
                  />
                </div>

                <div>
                  <label htmlFor="provenance.sellerContact" className="block text-sm font-medium text-slate-700">
                    Seller Contact
                  </label>
                  <input
                    type="text"
                    name="provenance.sellerContact"
                    id="provenance.sellerContact"
                    value={formData.provenance.sellerContact}
                    onChange={handleChange}
                    className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
                    placeholder="Phone or email"
                  />
                </div>

                <div>
                  <label htmlFor="provenance.dateAcquired" className="block text-sm font-medium text-slate-700">
                    Date Acquired
                  </label>
                  <input
                    type="date"
                    name="provenance.dateAcquired"
                    id="provenance.dateAcquired"
                    value={formData.provenance.dateAcquired}
                    onChange={handleChange}
                    className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
                  />
                </div>

                <div>
                  <label htmlFor="provenance.price" className="block text-sm font-medium text-slate-700">
                    Purchase Price (ZAR)
                  </label>
                  <AmountInput
                    id="provenance.price"
                    name="provenance.price"
                    value={formData.provenance.price}
                    onChange={(v) =>
                      handleChange({
                        target: { name: 'provenance.price', value: v != null ? String(v) : '' },
                      } as React.ChangeEvent<HTMLInputElement>)
                    }
                    min={0}
                  />
                </div>
              </div>
            </div>
          )}

          <div>
            <label htmlFor="provenance.notes" className="block text-sm font-medium text-slate-700">
              Provenance Notes
            </label>
            <textarea
              name="provenance.notes"
              id="provenance.notes"
              value={formData.provenance.notes}
              onChange={handleChange}
              rows={4}
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
              placeholder="Additional notes about the bird's origin, breeding history, or acquisition details..."
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function SaleInfoTab({ bird }: { bird: Bird }) {
  const saleInfo = bird.provenance?.saleInfo;
  
  if (!saleInfo && bird.status !== 'SOLD') {
    return (
      <div className="space-y-6">
        <div className="text-center py-12">
          <BanknotesIcon className="mx-auto h-12 w-12 text-slate-400" />
          <h3 className="mt-2 text-sm font-medium text-slate-900">No Sale Information</h3>
          <p className="mt-1 text-sm text-slate-500">
            This bird has not been sold yet. Sale information will appear here once the bird is sold through the transaction system.
          </p>
        </div>
      </div>
    );
  }

  if (bird.status === 'SOLD' && !saleInfo) {
    return (
      <div className="space-y-6">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">
                Sale Information Missing
              </h3>
              <div className="mt-2 text-sm text-yellow-700">
                <p>
                  This bird is marked as sold. Sale details are available in the Sales module. 
                  This may be because the bird was sold before the enhanced transaction system was implemented.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-slate-900 mb-4 flex items-center">
          <BanknotesIcon className="h-5 w-5 mr-2 text-green-600" />
          Sale Information
        </h3>
        
        <div className="bg-green-50 border border-green-200 rounded-lg p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Basic Sale Information */}
            <div className="space-y-4">
              <h4 className="text-md font-semibold text-green-900">Transaction Details</h4>
              
              {saleInfo?.dateSold && (
                <div>
                  <dt className="text-sm font-medium text-green-700">Date Sold</dt>
                  <dd className="mt-1 text-sm text-green-900 font-semibold">
                    {new Date(saleInfo.dateSold).toLocaleDateString()}
                  </dd>
                </div>
              )}
              
              {saleInfo?.salePrice && (
                <div>
                  <dt className="text-sm font-medium text-green-700">Sale Price</dt>
                  <dd className="mt-1 text-sm text-green-900 font-semibold">
                    R{saleInfo.salePrice.toLocaleString()}
                  </dd>
                </div>
              )}
              
              {saleInfo?.saleInvoiceNumber && (
                <div>
                  <dt className="text-sm font-medium text-green-700">Invoice Number</dt>
                  <dd className="mt-1 text-sm text-green-900 font-semibold">
                    {saleInfo.saleInvoiceNumber}
                  </dd>
                </div>
              )}
              
              {saleInfo?.salePaymentMethod && (
                <div>
                  <dt className="text-sm font-medium text-green-700">Payment Method</dt>
                  <dd className="mt-1 text-sm text-green-900 font-semibold">
                    {saleInfo.salePaymentMethod}
                  </dd>
                </div>
              )}
              
              {saleInfo?.salePaymentReference && (
                <div>
                  <dt className="text-sm font-medium text-green-700">Payment Reference</dt>
                  <dd className="mt-1 text-sm text-green-900 font-semibold">
                    {saleInfo.salePaymentReference}
                  </dd>
                </div>
              )}
            </div>

            {/* Buyer Information */}
            <div className="space-y-4">
              <h4 className="text-md font-semibold text-green-900">Buyer Information</h4>
              
              {saleInfo?.buyerName && (
                <div>
                  <dt className="text-sm font-medium text-green-700">Buyer Name</dt>
                  <dd className="mt-1 text-sm text-green-900 font-semibold">
                    {saleInfo.buyerName}
                  </dd>
                </div>
              )}
              
              {saleInfo?.buyerContact && (
                <div>
                  <dt className="text-sm font-medium text-green-700">Contact Information</dt>
                  <dd className="mt-1 text-sm text-green-900 font-semibold">
                    {saleInfo.buyerContact}
                  </dd>
                </div>
              )}
              
              {saleInfo?.buyerAddress && (
                <div>
                  <dt className="text-sm font-medium text-green-700">Address</dt>
                  <dd className="mt-1 text-sm text-green-900">
                    {saleInfo.buyerAddress}
                  </dd>
                </div>
              )}
            </div>
          </div>

          {/* Sale Notes */}
          {saleInfo?.saleNotes && (
            <div className="mt-6 pt-6 border-t border-green-200">
              <h4 className="text-md font-semibold text-green-900 mb-2">Sale Notes</h4>
              <p className="text-sm text-green-800 bg-green-100 p-3 rounded-md">
                {saleInfo.saleNotes}
              </p>
            </div>
          )}

          {/* Delivery Information */}
          {(saleInfo?.deliveryDate || saleInfo?.deliveryMethod || saleInfo?.trackingNumber) && (
            <div className="mt-6 pt-6 border-t border-green-200">
              <h4 className="text-md font-semibold text-green-900 mb-4">Delivery Information</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {saleInfo?.deliveryDate && (
                  <div>
                    <dt className="text-sm font-medium text-green-700">Delivery Date</dt>
                    <dd className="mt-1 text-sm text-green-900 font-semibold">
                      {new Date(saleInfo.deliveryDate).toLocaleDateString()}
                    </dd>
                  </div>
                )}
                
                {saleInfo?.deliveryMethod && (
                  <div>
                    <dt className="text-sm font-medium text-green-700">Delivery Method</dt>
                    <dd className="mt-1 text-sm text-green-900 font-semibold">
                      {saleInfo.deliveryMethod}
                    </dd>
                  </div>
                )}
                
                {saleInfo?.trackingNumber && (
                  <div>
                    <dt className="text-sm font-medium text-green-700">Tracking Number</dt>
                    <dd className="mt-1 text-sm text-green-900 font-semibold">
                      {saleInfo.trackingNumber}
                    </dd>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Transfer Information */}
          {(saleInfo?.transferCertificateNumber || saleInfo?.newOwnerLicense) && (
            <div className="mt-6 pt-6 border-t border-green-200">
              <h4 className="text-md font-semibold text-green-900 mb-4">Transfer Information</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {saleInfo?.transferCertificateNumber && (
                  <div>
                    <dt className="text-sm font-medium text-green-700">Transfer Certificate</dt>
                    <dd className="mt-1 text-sm text-green-900 font-semibold">
                      {saleInfo.transferCertificateNumber}
                    </dd>
                  </div>
                )}
                
                {saleInfo?.newOwnerLicense && (
                  <div>
                    <dt className="text-sm font-medium text-green-700">New Owner License</dt>
                    <dd className="mt-1 text-sm text-green-900 font-semibold">
                      {saleInfo.newOwnerLicense}
                    </dd>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* System Information */}
          <div className="mt-6 pt-6 border-t border-green-200">
            <h4 className="text-md font-semibold text-green-900 mb-4">System Information</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs text-green-700">
              {saleInfo?.saleTransactionId && (
                <div>
                  <dt className="font-medium">Transaction ID</dt>
                  <dd className="mt-1 font-mono">{saleInfo.saleTransactionId}</dd>
                </div>
              )}
              
              {saleInfo?.saleId && (
                <div>
                  <dt className="font-medium">Sale ID</dt>
                  <dd className="mt-1 font-mono">{saleInfo.saleId}</dd>
                </div>
              )}
              
              {saleInfo?.saleBatchNumber && (
                <div>
                  <dt className="font-medium">Batch Number</dt>
                  <dd className="mt-1 font-mono">{saleInfo.saleBatchNumber}</dd>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}